import { cn } from '@/lib/utils';
import {
  getGeographyFlag,
  getGeographyLabel,
  getGeographyOptions,
} from '@/shared/geography';
import { Check, ChevronDown, X } from 'lucide-react';
import { type FC, type ReactNode, useMemo, useState } from 'react';

import {
  FilterPopoverTrigger,
  type FilterPopoverTriggerVisualProps,
} from './filter-popover-trigger';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

// ── Shared list (dropdown content) ──────────────────────────────────────────

type CountrySelectListProps = {
  /** Restrict to these geography codes. Omit for all countries. */
  countries?: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  /** Close the popover after selecting (useful for single-select). */
  onClose?: () => void;
  /** If true, only one value can be selected at a time. */
  single?: boolean;
};

/**
 * Searchable country list — just the dropdown content.
 * Embed inside a Popover, FilterPopoverTrigger, or any container.
 */
export const CountrySelectList: FC<CountrySelectListProps> = ({
  countries,
  selected,
  onChange,
  onClose,
  single,
}) => {
  const [search, setSearch] = useState('');

  const options = useMemo(() => getGeographyOptions(countries), [countries]);

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const toggle = (code: string) => {
    if (single) {
      onChange(selected.includes(code) ? [] : [code]);
      onClose?.();
    } else {
      onChange(
        selected.includes(code)
          ? selected.filter(c => c !== code)
          : [...selected, code]
      );
    }
  };

  return (
    <div className="flex flex-col min-w-[200px]">
      <div className="border-b px-3 py-2">
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
      </div>
      <div className="max-h-[280px] overflow-y-auto p-1">
        {filtered.length === 0 && (
          <div className="py-4 text-center text-sm text-muted-foreground">
            No countries found.
          </div>
        )}
        {filtered.map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggle(opt.value)}
            className={cn(
              'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm',
              'hover:bg-accent cursor-default'
            )}
          >
            {opt.icon && (
              <img
                src={opt.icon}
                alt=""
                className="h-4 w-4 shrink-0 object-contain"
              />
            )}
            <span className="flex-1 text-left truncate">{opt.label}</span>
            {selected.includes(opt.value) && (
              <Check className="h-4 w-4 shrink-0 text-primary" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

// ── Standalone select (trigger + dropdown) ──────────────────────────────────

type BaseProps = {
  countries?: string[];
  placeholder?: string;
  className?: string;
};

type SingleSelectProps = BaseProps & {
  mode?: 'single';
  value: string | null;
  onChange: (value: string | null) => void;
};

type MultiSelectProps = BaseProps & {
  mode: 'multi';
  value: string[];
  onChange: (value: string[]) => void;
};

type CountrySelectProps = SingleSelectProps | MultiSelectProps;

export const CountrySelect: FC<CountrySelectProps> = props => {
  const {
    countries,
    placeholder = 'Select country...',
    className,
    mode = 'single',
  } = props;

  const [open, setOpen] = useState(false);

  const selectedArray =
    mode === 'multi'
      ? (props as MultiSelectProps).value
      : (props as SingleSelectProps).value
        ? [(props as SingleSelectProps).value!]
        : [];

  const handleChange = (selected: string[]) => {
    if (mode === 'multi') {
      (props as MultiSelectProps).onChange(selected);
    } else {
      (props as SingleSelectProps).onChange(selected[0] ?? null);
    }
  };

  const canClear = selectedArray.length > 0;

  const handleClear = () => {
    if (mode === 'multi') (props as MultiSelectProps).onChange([]);
    else (props as SingleSelectProps).onChange(null);
  };

  const triggerContent = () => {
    if (selectedArray.length === 0)
      return <span className="text-muted-foreground">{placeholder}</span>;
    if (selectedArray.length === 1)
      return <FlagLabel code={selectedArray[0]!} />;
    return <span>{selectedArray.length} countries</span>;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={cn('relative', className)}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 py-1.5 text-sm shadow-sm',
              'ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring'
            )}
          >
            <span className="flex-1 text-left truncate">
              {triggerContent()}
            </span>
            {canClear ? (
              <X
                className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
                onClick={e => {
                  e.stopPropagation();
                  handleClear();
                }}
              />
            ) : (
              <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
          sideOffset={4}
        >
          <CountrySelectList
            countries={countries}
            selected={selectedArray}
            onChange={handleChange}
            onClose={() => setOpen(false)}
            single={mode !== 'multi'}
          />
        </PopoverContent>
      </div>
    </Popover>
  );
};

const FlagLabel: FC<{ code: string }> = ({ code }) => {
  const flag = getGeographyFlag(code);
  return (
    <span className="inline-flex items-center gap-2">
      {flag && (
        <img src={flag} alt="" className="h-4 w-4 shrink-0 object-contain" />
      )}
      {getGeographyLabel(code)}
    </span>
  );
};

// ── Filter pill variant (for filter bars) ───────────────────────────────────

type CountryFilterProps = {
  label: string;
  countries?: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  size?: 'default' | 'sm';
  filterPopoverProps?: FilterPopoverTriggerVisualProps;
  /**
   * When set and the filter is active, replaces the default "1 label / N selected" summary.
   */
  activePreview?: ReactNode;
};

/**
 * Country multi-select rendered as a filter pill (FilterPopoverTrigger style).
 * Drop-in replacement for OptionFilter when filtering by geography.
 */
export const CountryFilter: FC<CountryFilterProps> = ({
  label,
  countries,
  selected,
  onChange,
  size = 'default',
  filterPopoverProps,
  activePreview,
}) => {
  const isActive = selected.length > 0;

  const activeContent =
    activePreview !== undefined ? (
      activePreview
    ) : selected.length === 0 ? undefined : selected.length === 1 ? (
      <span className="inline-flex items-center gap-1.5 min-w-0 max-w-[220px]">
        {(() => {
          const flag = getGeographyFlag(selected[0]!);
          return flag ? (
            <img
              src={flag}
              alt=""
              className="h-3.5 w-3.5 shrink-0 object-contain"
            />
          ) : null;
        })()}
        <span className="truncate">{getGeographyLabel(selected[0]!)}</span>
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 min-w-0">
        <span className="inline-flex items-center gap-0.5">
          {selected
            .slice(0, selected.length > 3 ? 3 : selected.length)
            .map(code => {
              const flag = getGeographyFlag(code);
              return flag ? (
                <img
                  key={code}
                  src={flag}
                  alt=""
                  className="h-3.5 w-3.5 shrink-0 object-contain"
                />
              ) : (
                <span
                  key={code}
                  className="h-3.5 w-3.5 shrink-0 rounded-full bg-neutral-200"
                  aria-hidden
                />
              );
            })}
        </span>
        <span className="tabular-nums">{selected.length} selected</span>
      </span>
    );

  return (
    <FilterPopoverTrigger
      label={label}
      isActive={isActive}
      operator={
        isActive ? (selected.length === 1 ? 'is' : 'is any of') : undefined
      }
      activeContent={activeContent}
      onClear={isActive ? () => onChange([]) : undefined}
      size={size}
      {...filterPopoverProps}
    >
      <CountrySelectList
        countries={countries}
        selected={selected}
        onChange={onChange}
      />
    </FilterPopoverTrigger>
  );
};
